<section class="requests-page" aria-labelledby="page-title">
  <header class="page-header flex align-start justify-between gap-16 mb-20">
    <div>
      <h1 id="page-title" class="page-title fs-22 fw-700 m-0">Proxy Queue</h1>
      <p class="page-subtitle m-0 mt-5">
        Manage proxy access requests where staff initiate or verify patient-authorized portal
        delegation.
      </p>
    </div>

    <!-- Disabled until the manual-add modal lands (PBI "Proxy Queue - 04").
         openManualAddProxy() is still a no-op, and an enabled button that does
         nothing reads as a failed save. Drop the disabled attribute and the
         title when the modal is wired up. -->
    <button
      type="button"
      class="primary-button button--compact page-header__action hidden-feature"
      disabled
      title="Manually adding a proxy isn't available yet."
      (click)="openManualAddProxy()"
    >
      Manually Add Proxy
    </button>
  </header>

  <section class="queue-card" aria-labelledby="queue-heading">
    <h2 id="queue-heading" class="sr-only">Caregiver and proxy verifications queue</h2>

    <!-- Same inline filter bar as the linking queue (see new-linking-requests.html):
         the dropdowns apply on change, the search box waits for Filter / Enter. -->
    <div class="filter-bar flex gap-12 align-center flex-wrap">
      <app-dropdown
        label="Status"
        ariaLabel="Filter proxy requests by status"
        [options]="statusFilterOptions"
        [value]="selectedStatus()"
        (valueChange)="onStatusChange($event)"
      />

      <app-dropdown
        label="Date Range"
        ariaLabel="Filter proxy requests by date range"
        [options]="dateRangeOptions"
        [value]="selectedDateRange()"
        (valueChange)="onDateRangeChange($event)"
      />

      <div class="filter-divider" aria-hidden="true"></div>

      <div class="filter-control--search">
        <app-input label="Search" inputId="proxy-search" icon="search">
          <input
            id="proxy-search"
            type="search"
            [ngModel]="searchTerm()"
            (ngModelChange)="onSearchChange($event)"
            (keydown.enter)="applyFilters()"
            placeholder="Search proxy requests…"
            aria-label="Search proxy requests by patient or proxy user name"
            [attr.aria-invalid]="searchValidationError() ? 'true' : null"
            [attr.aria-describedby]="searchValidationError() ? 'proxy-search-error' : null"
          />
          @if (searchTerm()) {
          <app-icon-button
            class="search-clear"
            icon="close"
            size="xs"
            ariaLabel="Clear search"
            color="on-surface-variant"
            (clicked)="clearSearch()"
          />
          }
        </app-input>
        @if (searchValidationError(); as validationMessage) {
        <p id="proxy-search-error" class="search-validation-error m-0 mt-5 fs-12" role="alert">
          {{ validationMessage }}
        </p>
        }
      </div>

      <button
        type="button"
        class="primary-button button--compact filter-apply"
        (click)="applyFilters()"
        [disabled]="isLoading()"
      >
        Filter
      </button>
    </div>

    <!-- Lives outside the @if blocks below on purpose: an aria-live region has to
         already be in the DOM for a change to be announced, so a region that is
         created at the same moment its text appears announces nothing. -->
    <p class="sr-only" role="status" aria-live="polite">{{ queueStatusAnnouncement() }}</p>

    <!-- The shell is measured to size the page (ProxyVerifications.TABLE_CHROME_PX)
         and the queue's first load waits on that measurement, so it must stay
         outside the state branches below: measuring an element that only exists
         once rows render would need rows on screen first, which costs a second
         PHI-bearing request per visit. -->
    <div class="table-shell" #tableShell>
      @if (isLoading()) {
      <div class="table-status flex flex-col align-center justify-center text-center">
        <app-progress-spinner [diameter]="48" />
        <p>Loading verifications…</p>
      </div>
      } @else if (loadError(); as loadError) {
      <div class="table-status flex flex-col align-center justify-center text-center" role="alert">
        <h3>Unable to load verifications</h3>
        <p>{{ loadError }}</p>
        <button type="button" class="primary-button button--compact" (click)="retry()">
          Try again
        </button>
      </div>
      } @else if (allVerifications().length === 0) {
      <div class="table-status flex flex-col align-center justify-center text-center">
        @if (hasActiveSearch()) {
        <h3>No search results</h3>
        <p>
          Your search did not return any proxy requests. Try a different patient or proxy user name.
        </p>
        } @else if (hasActiveFilters()) {
        <h3>No matching requests</h3>
        <p>
          There are no proxy requests within the selected filters. Try a different status, or
          broaden the date range.
        </p>
        } @else {
        <h3>No proxy requests</h3>
        <p>There are currently no proxy access requests to review.</p>
        }
      </div>
      } @else {
      <div class="queue-table" role="table" aria-label="Proxy access requests">
        <div class="queue-table__header queue-table__grid gap-12 fw-600 uppercase" role="row">
          <div role="columnheader">Request Date</div>
          <div role="columnheader">Patient</div>
          <div role="columnheader">Identifiers</div>
          <div role="columnheader">Proxy User</div>
          <div role="columnheader">Relationship</div>
          <div role="columnheader">Status</div>
          <div class="hidden-feature" role="columnheader">Actions</div>
        </div>

        <div class="queue-table__body" role="rowgroup">
          @for (verification of allVerifications(); track verification.id) {
          <!-- role="row", not role="button": a rowgroup only accepts rows, and a
               button flattens its subtree into its accessible name, which would
               drop identifiers / proxy user / relationship / status from the
               accessibility tree entirely. The row is still click- and
               keyboard-activatable via tabindex + the keydown handler. -->
          <div
            class="queue-table__row queue-table__grid align-center gap-12 cursor"
            role="row"
            tabindex="0"
            [attr.aria-label]="verification.ariaLabel"
            (click)="openRequest(verification)"
            (keydown)="onRowKeydown($event, verification)"
          >
            <div class="cell" role="cell">
              <div class="cell--primary">{{ verification.submittedDate }}</div>
              <div class="cell-sub">{{ verification.submittedTime }}</div>
            </div>
            <div class="cell" role="cell">
              <div class="cell--primary fw-600">{{ verification.patientName }}</div>
              @if (verification.isMinor) {
              <div class="patient-meta flex align-center gap-8">
                <span class="badge badge--minor">
                  <app-icon
                    class="badge__icon"
                    name="warning"
                    size="custom"
                    customSize="12px"
                    color="custom"
                    customColor="currentColor"
                    role="presentation"
                  ></app-icon>
                  Minor
                </span>
                @if (verification.patientDob) {
                <span class="cell-sub">DOB {{ verification.patientDob }}</span>
                }
              </div>
              }
            </div>
            <div class="cell cell--primary identifier-cell" role="cell">
              @for (identifier of verification.identifiers; track identifier) {
              <span>{{ identifier }}</span>
              }
            </div>
            <div class="cell cell--primary" role="cell">{{ verification.caregiverName }}</div>
            <div class="cell cell--primary" role="cell">{{ verification.relationship }}</div>
            <div class="cell" role="cell">
              <span class="badge" [class]="'badge--' + verification.statusKind"
                >{{ verification.statusLabel }}</span
              >
            </div>
            <!-- Both email actions render but stay disabled until their modal +
                 endpoints land (PBI "Proxy Queue - 03"): the row advertises which
                 action it will offer without an enabled control that would read as
                 "the email was sent" while doing nothing. A disabled button
                 dispatches no click, so neither the stopPropagation nor the row's
                 own navigation can fire from it. Drop the disabled attribute and
                 the title on each when the modal is wired up — see
                 ProxyVerifications.sendAuthEmail() / resendAuthEmail(). -->
            <div class="cell cell--actions hidden-feature" role="cell">
              @if (verification.isMinor) {
              <span class="action-note">Email auth not required</span>
              } @else if (verification.canSendAuthEmail) {
              <button
                type="button"
                class="primary-button button--compact action-button"
                disabled
                title="Sending the authorization email isn't available yet."
                [attr.aria-label]="verification.sendAuthEmailAriaLabel"
                (click)="sendAuthEmail(verification); $event.stopPropagation()"
              >
                Send Auth Email
              </button>
              } @else if (verification.canResendEmail) {
              <button
                type="button"
                class="secondary-button button--compact action-button"
                disabled
                title="Resending the authorization email isn't available yet."
                [attr.aria-label]="verification.resendEmailAriaLabel"
                (click)="resendAuthEmail(verification); $event.stopPropagation()"
              >
                Resend Email
              </button>
              }
            </div>
          </div>
          }
        </div>
      </div>
      }

      <!-- Outside the branches above so an empty result set still has controls:
           filtering (or a deletion) while on page 3 can empty the current page,
           and pagination trapped inside the non-empty branch would strand the
           user on "No proxy requests" with no way back to page 1. -->
      @if (!isLoading() && !loadError()) {
      <footer class="pagination flex align-center justify-between gap-12" aria-label="Pagination">
        <p class="pagination__summary m-0 fs-12">{{ showingSummary() }}</p>

        <nav
          class="pagination__controls flex flex-wrap justify-end gap-4"
          aria-label="Page controls"
        >
          <button
            type="button"
            class="page-button cursor"
            (click)="goToPage(1)"
            [disabled]="currentPage() === 1"
            aria-label="First page"
          >
            <app-icon
              name="first_page"
              size="sm"
              color="custom"
              customColor="currentColor"
              role="presentation"
            />
          </button>
          <button
            type="button"
            class="page-button cursor"
            (click)="goToPage(currentPage() - 1)"
            [disabled]="currentPage() === 1"
            aria-label="Previous page"
          >
            <app-icon
              name="chevron_left"
              size="sm"
              color="custom"
              customColor="currentColor"
              role="presentation"
            />
          </button>

          @for (pageItem of pageItems(); track $index) { @if (pageItem === 'ellipsis') {
          <span class="pagination__ellipsis fs-12" aria-hidden="true">…</span>
          } @else {
          <button
            type="button"
            class="page-button fs-12 fw-600 cursor"
            [class.page-button--active]="pageItem === currentPage()"
            [attr.aria-current]="pageItem === currentPage() ? 'page' : null"
            (click)="goToPage(pageItem)"
          >
            {{ pageItem }}
          </button>
          } }

          <button
            type="button"
            class="page-button cursor"
            (click)="goToPage(currentPage() + 1)"
            [disabled]="currentPage() === totalPages()"
            aria-label="Next page"
          >
            <app-icon
              name="chevron_right"
              size="sm"
              color="custom"
              customColor="currentColor"
              role="presentation"
            />
          </button>
          <button
            type="button"
            class="page-button cursor"
            (click)="goToPage(totalPages())"
            [disabled]="currentPage() === totalPages()"
            aria-label="Last page"
          >
            <app-icon
              name="last_page"
              size="sm"
              color="custom"
              customColor="currentColor"
              role="presentation"
            />
          </button>
        </nav>
      </footer>
      }
    </div>
  </section>
</section>
